import type { StoryCtx } from '@dispositio/core';
import type { EntityTab, SceneTab, Selection, useWorkspace } from '../state/workspace';

/**
 * Bausteine der Führungen durch die Tutorials (Rundgang, Fehlersuche, Verknüpfungen; die Schritte je Vorlage in guides.ts): Schritt für
 * Schritt ein Erklärtext, blau hervorgehobene Ziele und – wo der Nutzer etwas tun soll – eine Bedingung, bei der es von selbst
 * weitergeht. Der Stand liegt je Projekt im Browser (`dispositio.tutorial.<projekt>`).
 */

/** Was die Schritte vom Stand der App sehen */
export interface GuideEnv {
  ctx: StoryCtx;
  ws: ReturnType<typeof useWorkspace.getState>;
  /** Stand der im Manuskript geöffneten Szene beim Betreten der Schritte mit `remember` (je Schritt), um Neues zu erkennen */
  memo: Record<string, SceneMemo>;
}

/** Was sich ein Schritt beim Betreten merkt: Nennungen, Bezüge und ignorierte Stellen im geöffneten Dokument, Beziehungen und Änderungen */
export interface SceneMemo {
  /** Schlüssel der Nennungen im Text (je Nennung einer) */
  links: string[];
  /** erster Schlüssel je Bezug im Text */
  spans: string[];
  /** bewusst nicht verknüpfte Stellen des Dokuments */
  ignored: number;
  /** Beziehungen und Änderungen (Zustand) der ganzen Geschichte */
  facts: number;
}

/** Ziel einer Hervorhebung: Elemente der Seite (CSS-Selektor, mehrere ergeben einen Rahmen um alle) oder Elemente der Zeichenfläche */
export type GuideTarget = { selector: string } | { nodeId: string; roles: readonly string[] };

export interface GuideStep {
  id: string;
  text: (env: GuideEnv) => string;
  /** der Rahmen, neben dem die Textbox steht: das erste Ziel, das sich findet */
  targets?: (env: GuideEnv) => GuideTarget[];
  /** weitere Rahmen, je Eintrag das erste Ziel, das sich findet */
  also?: (env: GuideEnv) => GuideTarget[][];
  /** „Weiter“ erst, wenn das gilt – dann geht es von selbst weiter */
  until?: (env: GuideEnv) => boolean;
  /** geht von selbst weiter, sobald das gilt; „Weiter“ geht auch vorher */
  auto?: (env: GuideEnv) => boolean;
  /** „Weiter“ erst, wenn das einmal gegolten hat – dann bleibt es frei, weiter geht es aber erst mit „Weiter“ (Zeit zum Ansehen) */
  unlock?: (env: GuideEnv) => boolean;
  /**
   * beim Betreten (und beim Fortsetzen nach dem Neuladen): Fenster schließen, Bereich, Auswahl bzw. Tab (einer Szene, einer Entität)
   * wählen – `select` stellt her, was der Schritt in der Seitenleiste zeigt
   */
  enter?: { closeDialog?: boolean; designFocus?: 'person'; select?: (env: GuideEnv) => Selection; sceneTab?: SceneTab; entityTab?: EntityTab };
  /** beim Betreten die Objekte der geöffneten Szene merken (`memo`) */
  remember?: boolean;
  /**
   * Lage der Textbox: oben rechts auf der Zeichenfläche, unter der Modusleiste oder oben in der rechten Spalte (über der Seitenleiste –
   * beim Schreiben bleibt so die Seite frei)
   */
  dock?: GuideDock;
  /** der Rahmen blinkt (statt nur zu leuchten) */
  blink?: boolean;
  /** Lage der Textbox neben dem Ziel, wenn sie passt (`right` bzw. `left`: rechts bzw. links daneben, oben bündig) */
  place?: 'right' | 'left';
  /**
   * Was in diesem Schritt bedienbar ist (CSS-Selektoren; dazu immer die Textbox). Alles andere ist gesperrt: Klicks und Tasten dort
   * kommen nicht an. Ohne Angabe ist nur die Textbox bedienbar; `free` gibt alles frei.
   */
  allow?: string[];
  free?: boolean;
  /** sichtbar gesperrt (inert, blass): z. B. das Fenster einer neuen Person, das nur gezeigt wird */
  freeze?: string[];
  /** zurück zum vorigen Schritt, sobald das gilt (z. B. der Browser wurde ohne Wahl geschlossen) */
  back?: (env: GuideEnv) => boolean;
}

export type GuideDock = 'canvas' | 'modes' | 'side';

/** das im Manuskript geöffnete Dokument und sein Text (gespeicherter Stand) */
const openDocOf = (env: GuideEnv) => {
  const open = env.ws.openDoc;
  return open ? env.ctx.nodes.get(open.nodeId)?.documents.find((d) => d.id === open.docId) : undefined;
};
const openText = (env: GuideEnv): string => openDocOf(env)?.text ?? '';
/** Schlüssel der Nennungen (`Wort[@k]`, `[Wendung](@k)`) bzw. Zahl der Bezüge (`[Stelle]{@k …}`) im geöffneten Dokument */
const LINKS = /\[@([a-z][a-z0-9_]*)\]|\]\(@([a-z][a-z0-9_]*)\)/g;
const linkKeys = (text: string): string[] => [...text.matchAll(LINKS)].map((m) => m[1] ?? m[2]!);
const spanKeys = (text: string): string[] => [...text.matchAll(/\]\{@([a-z][a-z0-9_]*)/g)].map((m) => m[1]!);

/** Zahl der Beziehungen (an den Entitäten) und Änderungen (in den Szenen) der Geschichte */
const factsOf = (ctx: StoryCtx): number =>
  ctx.story.entities.reduce((n, e) => n + (e.relations?.length ?? 0), 0) + ctx.story.nodes.reduce((n, x) => n + (x.changes?.length ?? 0), 0);

/** Stand der geöffneten Szene, den sich ein Schritt mit `remember` beim Betreten merkt */
export function sceneMemo(env: GuideEnv): SceneMemo {
  const text = openText(env);
  return { links: linkKeys(text), spans: spanKeys(text), ignored: openDocOf(env)?.ignored?.length ?? 0, facts: factsOf(env.ctx) };
}
/** Schlüssel in `now`, die über `before` hinaus dazugekommen sind (je Vorkommen gezählt) */
function newKeys(before: string[], now: string[]): string[] {
  const left = new Map<string, number>();
  for (const k of before) left.set(k, (left.get(k) ?? 0) + 1);
  return now.filter((k) => {
    const n = left.get(k) ?? 0;
    left.set(k, n - 1);
    return n <= 0;
  });
}
/**
 * Seit dem Betreten des Schritts `stepId` im Text neu verknüpfte Schlüssel (auch von Elementen, die schon in der Szene sind). Am Text
 * erkannt, nicht an den Objekten der Szene: Der Text steht sofort da, das Objekt kommt erst mit der Antwort des Servers.
 */
export function linkedSince(env: GuideEnv, stepId: string): string[] {
  const memo = env.memo[stepId];
  return memo ? newKeys(memo.links, linkKeys(openText(env))) : [];
}
/** seit dem Betreten des Schritts `stepId` neu zugeordnete Bezüge (ihr erster Schlüssel) */
export const spannedSince = (env: GuideEnv, stepId: string): string[] => {
  const memo = env.memo[stepId];
  return memo ? newKeys(memo.spans, spanKeys(openText(env))) : [];
};
/** seit dem Betreten des Schritts `stepId` eine Stelle bewusst nicht verknüpft (✕ am Textmarker) */
export const newIgnored = (env: GuideEnv, stepId: string): boolean => (openDocOf(env)?.ignored?.length ?? 0) > (env.memo[stepId]?.ignored ?? Infinity);
/** seit dem Betreten des Schritts `stepId` eine Beziehung oder Änderung festgehalten (aus dem Text oder der Eingabezeile) */
export const newFacts = (env: GuideEnv, stepId: string): boolean => factsOf(env.ctx) > (env.memo[stepId]?.facts ?? Infinity);
// ---------------------------------------------------------------------------
// Stand im Browser
// ---------------------------------------------------------------------------

const PREFIX = 'dispositio.tutorial.';
/** ohne Speicher im Browser (privates Fenster): gilt bis zum Neuladen */
const memory = new Map<string, string>();

function store(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
function read(key: string): string | null {
  try {
    return store()?.getItem(key) ?? memory.get(key) ?? null;
  } catch {
    return memory.get(key) ?? null;
  }
}
function write(key: string, value: string | null): void {
  if (value === null) memory.delete(key);
  else memory.set(key, value);
  try {
    if (value === null) store()?.removeItem(key);
    else store()?.setItem(key, value);
  } catch {
    // der Stand gilt dann nur bis zum Neuladen
  }
}

/** Aktueller Schritt der Führung in diesem Projekt; null = keine Führung (nie gestartet oder beendet) */
export function guideStepOf(projectId: string, steps: number): number | null {
  const raw = read(PREFIX + projectId);
  if (raw === null) return null;
  try {
    const step = Number((JSON.parse(raw) as { step?: unknown }).step);
    return Number.isInteger(step) && step >= 0 && step < steps ? step : 0;
  } catch {
    return 0;
  }
}

export function saveGuideStep(projectId: string, step: number): void {
  write(PREFIX + projectId, JSON.stringify({ step }));
}

export function endGuide(projectId: string): void {
  write(PREFIX + projectId, null);
}

/** „Tutorial starten“: der Stand des ersetzten Projekts (`previous`) fällt weg, das neue beginnt beim ersten Schritt */
export function startGuide(projectId: string, previous: string | null = null): void {
  if (previous) endGuide(previous);
  saveGuideStep(projectId, 0);
}

// ---------------------------------------------------------------------------
// Lage der Textbox
// ---------------------------------------------------------------------------

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Rahmen um mehrere Rechtecke */
export function unionBox(boxes: Box[]): Box | null {
  if (boxes.length === 0) return null;
  const left = Math.min(...boxes.map((b) => b.left));
  const top = Math.min(...boxes.map((b) => b.top));
  const right = Math.max(...boxes.map((b) => b.left + b.width));
  const bottom = Math.max(...boxes.map((b) => b.top + b.height));
  return { left, top, width: right - left, height: bottom - top };
}

/** feste Lage der Textbox an einer Fläche: in einer freien Ecke darin, mittig darunter oder rechts bündig darin (oben, sonst unten) */
export interface Dock {
  box: Box;
  at: 'corner' | 'below' | 'column';
}

/** Überdeckt die Textbox an `p` den Rahmen um `b` (samt seinem Abstand)? */
const covers = (p: { left: number; top: number }, card: { width: number; height: number }, b: Box, pad = 8): boolean =>
  p.left < b.left + b.width + pad && p.left + card.width > b.left - pad && p.top < b.top + b.height + pad && p.top + card.height > b.top - pad;
/** wie viel Fläche die Textbox an `p` von `b` verdeckt */
const coveredArea = (p: { left: number; top: number }, card: { width: number; height: number }, b: Box): number =>
  Math.max(0, Math.min(p.left + card.width, b.left + b.width) - Math.max(p.left, b.left)) * Math.max(0, Math.min(p.top + card.height, b.top + b.height) - Math.max(p.top, b.top));

/**
 * Lage der Textbox: neben dem Ziel, ohne es zu verdecken – bei flachen Zielen darunter oder darüber, bei hohen (Seitenleiste, Seite des
 * Manuskripts) rechts oder links daneben, je nachdem, was zuerst ganz ins Fenster passt; passt nichts, die Lage im Fenster, die die
 * Rahmen am wenigsten verdeckt (bei einem Ziel, das fast das ganze Fenster füllt, oben rechts darin); ohne Ziel unten in der Mitte.
 * Ein `dock` legt die Lage fest, solange sie keinen Rahmen (`avoid`) verdeckt – sonst gilt die Lage neben dem Ziel: in einer Ecke der
 * Zeichenfläche die erste freie (oben rechts links neben dem Knopf „+“, unten rechts, unten links, oben links), in der rechten Spalte oben,
 * sonst unten. `prefer`: zuerst rechts bzw. links neben dem Ziel, oben bündig (links etwa bei Zielen in der Seitenleiste, damit die
 * Textbox nicht verdeckt, was darunter steht).
 */
export function placeCard(
  target: Box | null,
  card: { width: number; height: number },
  view: { width: number; height: number },
  dock?: Dock | null,
  avoid: Box[] = [],
  prefer: 'right' | 'left' | null = null,
  gap = 14,
): { left: number; top: number } {
  const m = 12;
  const clampX = (x: number): number => Math.min(Math.max(m, x), Math.max(m, view.width - card.width - m));
  const clampY = (y: number): number => Math.min(Math.max(m, y), Math.max(m, view.height - card.height - m));
  const free = (p: { left: number; top: number }): boolean => !avoid.some((b) => covers(p, card, b));
  if (dock) {
    const d = dock.box;
    const spots =
      dock.at === 'corner'
        ? [
            { left: d.left + d.width - card.width - 72, top: d.top + 16 },
            { left: d.left + d.width - card.width - 16, top: d.top + d.height - card.height - 16 },
            { left: d.left + 16, top: d.top + d.height - card.height - 16 },
            { left: d.left + 16, top: d.top + 16 },
          ]
        : dock.at === 'column'
          ? [
              { left: d.left + d.width - card.width - 12, top: d.top + 12 },
              { left: d.left + d.width - card.width - 12, top: d.top + d.height - card.height - 12 },
            ]
          : [{ left: d.left + d.width / 2 - card.width / 2, top: d.top + d.height + gap }];
    const spot = spots.map((c) => ({ left: clampX(c.left), top: clampY(c.top) })).find(free);
    if (spot) return spot;
  }
  if (!target) return { left: clampX((view.width - card.width) / 2), top: clampY(view.height - card.height - 40) };
  const t = target;
  const below = { left: clampX(t.left), top: t.top + t.height + gap };
  const above = { left: clampX(t.left), top: t.top - card.height - gap };
  const right = { left: t.left + t.width + gap, top: clampY(t.top) };
  const left = { left: t.left - card.width - gap, top: clampY(t.top) };
  const sides = prefer === 'right' ? [right, below, above, left] : prefer === 'left' ? [left, below, above, right] : t.height > view.height * 0.4 ? [right, left, below, above] : [below, above, right, left];
  const fits = (p: { left: number; top: number }): boolean => p.left >= m && p.top >= m && p.left + card.width <= view.width - m && p.top + card.height <= view.height - m;
  const fit = sides.find((p) => fits(p) && free(p)) ?? sides.find(fits);
  if (fit) return fit;
  // nichts passt ganz daneben: ins Fenster gerückt, wo am wenigsten verdeckt ist (bei Gleichstand in der Reihenfolge oben) – die weiteren
  // Rahmen (etwa das ✕ eines Fensters, das zu schließen ist) bleiben dabei frei, wo es geht
  const others = avoid.filter((b) => b.left !== t.left || b.top !== t.top || b.width !== t.width || b.height !== t.height);
  const covered = (p: { left: number; top: number }): number => [t, ...others].reduce((n, b) => n + coveredArea(p, card, b), 0);
  const inView = sides.map((p) => ({ left: clampX(p.left), top: clampY(p.top) }));
  const open = inView.filter((p) => !others.some((b) => covers(p, card, b)));
  return (open.length > 0 ? open : inView).reduce((best, p) => (covered(p) < covered(best) ? p : best));
}
